import React, { useState, useEffect, useRef } from 'react';
import { 
  Card, Button, Space, Table, message, Row, Col, 
  InputNumber, Input, Select, Modal, Form, Tag, Alert, 
  Radio, Tooltip, Progress, Divider
} from 'antd';
import { 
  CopyOutlined, DownloadOutlined, WalletOutlined, SearchOutlined,
  ReloadOutlined, DeleteOutlined, FilterOutlined, ExportOutlined,
  LinkOutlined, PlusOutlined
} from '@ant-design/icons';
import { ethers } from 'ethers';
import { saveAs } from 'file-saver';

// 接口定义
interface WalletEntry {
  id: number;
  address: string;
  balance: string;
  status: 'idle' | 'loading' | 'success' | 'error';
  isSelected: boolean;
}

interface TokenInfo {
  address: 'native' | string;
  symbol: string;
  decimals: number;
  name: string;
}

interface ConfigState {
  rpcUrl: string;
  queryMode: 'compatible' | 'speed';
  batchSize: number;
  token: TokenInfo;
}

interface QueryProgress {
  checked: number;
  total: number;
}

// 添加自定义网络接口
interface CustomNetwork {
  id: string;
  name: string;
  rpcUrl: string;
  chainId: number;
  symbol: string;
  explorer?: string;
  isCustom: true;
}

// 预设网络接口
interface PresetNetwork {
  id: string;
  name: string;
  rpcUrl: string;
  chainId: number;
  symbol: string;
  explorer: string;
  isCustom: false;
}

// 网络联合类型
type NetworkConfig = PresetNetwork | CustomNetwork;

// 预设网络配置
const NETWORK_CONFIGS = [
  {
    id: 'eth-mainnet',
    name: 'Ethereum Mainnet',
    rpcUrl: 'https://eth.llamarpc.com',
    chainId: 1,
    symbol: 'ETH',
    explorer: 'https://etherscan.io/address/',
    isCustom: false as const
  },
  {
    id: 'bsc-mainnet',
    name: 'BNB Smart Chain',
    rpcUrl: 'https://bsc-dataseed2.bnbchain.org',
    chainId: 56,
    symbol: 'BNB',
    explorer: 'https://bscscan.com/address/',
    isCustom: false as const
  },
  {
    id: 'polygon-mainnet',
    name: 'Polygon Mainnet',
    rpcUrl: 'https://polygon-rpc.com',
    chainId: 137,
    symbol: 'MATIC',
    explorer: 'https://polygonscan.com/address/',
    isCustom: false as const
  },
  {
    id: 'arbitrum-one',
    name: 'Arbitrum One',
    rpcUrl: 'https://arb1.arbitrum.io/rpc',
    chainId: 42161,
    symbol: 'ETH',
    explorer: 'https://arbiscan.io/address/',
    isCustom: false as const
  },
  {
    id: 'base-mainnet',
    name: 'Base',
    rpcUrl: 'https://mainnet.base.org',
    chainId: 8453,
    symbol: 'ETH',
    explorer: 'https://basescan.org/address/',
    isCustom: false as const
  },
  {
    id: 'optimism-mainnet',
    name: 'Optimism',
    rpcUrl: 'https://mainnet.optimism.io',
    chainId: 10,
    symbol: 'ETH',
    explorer: 'https://optimistic.etherscan.io/address/',
    isCustom: false as const
  },
];

// ERC20 ABI
const ERC20_ABI = [
  "function name() view returns (string)",
  "function symbol() view returns (string)",
  "function decimals() view returns (uint8)",
  "function balanceOf(address account) view returns (uint256)"
];

const EvmBalanceChecker: React.FC = () => {
  // 状态管理
  const [wallets, setWallets] = useState<WalletEntry[]>([]);
  const [config, setConfig] = useState<ConfigState>({
    rpcUrl: NETWORK_CONFIGS[0].rpcUrl,
    queryMode: 'compatible',
    batchSize: 1,
    token: {
      address: 'native',
      symbol: NETWORK_CONFIGS[0].symbol,
      decimals: 18,
      name: 'Native Token'
    }
  });
  const [customTokens, setCustomTokens] = useState<TokenInfo[]>([]);
  const [queryProgress, setQueryProgress] = useState<QueryProgress>({ checked: 0, total: 0 });
  const [totalBalance, setTotalBalance] = useState<string>('0');
  const [isQuerying, setIsQuerying] = useState(false);
  const [currentExplorer, setCurrentExplorer] = useState(NETWORK_CONFIGS[0].explorer);
  
  // 自定义网络状态管理
  const [customNetworks, setCustomNetworks] = useState<CustomNetwork[]>([]);
  const [allNetworks, setAllNetworks] = useState<NetworkConfig[]>(() => [...NETWORK_CONFIGS]);
  
  // Modal 状态
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [isTokenModalVisible, setIsTokenModalVisible] = useState(false);
  const [isCustomNetworkModalVisible, setIsCustomNetworkModalVisible] = useState(false);
  const [addressesInput, setAddressesInput] = useState('');
  const [tokenAddressInput, setTokenAddressInput] = useState('');
  const [filterValue, setFilterValue] = useState<string>('');
  
  // 自定义网络表单状态
  const [customNetworkForm, setCustomNetworkForm] = useState({
    name: '',
    rpcUrl: '',
    explorer: '',
    isLoading: false
  });
  const [chainInfo, setChainInfo] = useState<{
    chainId: number | null;
    symbol: string;
    isValid: boolean;
  }>({
    chainId: null,
    symbol: '',
    isValid: false
  });

  // 样式定义
  const buttonStyle = {
    borderRadius: '6px',
    fontWeight: '500',
    fontSize: '14px',
    height: '36px',
    minWidth: '80px'
  };

  const primaryButtonStyle = {
    ...buttonStyle,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    border: 'none',
    color: 'white',
    boxShadow: '0 2px 4px rgba(102, 126, 234, 0.2)'
  };

  const componentStyle = {
    borderRadius: '6px',
    fontSize: '14px'
  };

  // 工具函数
  const isValidAddress = (address: string): boolean => {
    return ethers.isAddress(address);
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    message.success(`${label}已复制到剪贴板`);
  };

  // 获取链信息的函数
  const fetchChainInfo = async (rpcUrl: string) => {
    if (!rpcUrl.trim()) {
      setChainInfo({ chainId: null, symbol: '', isValid: false });
      return;
    }

    try {
      setCustomNetworkForm(prev => ({ ...prev, isLoading: true }));
      
      const provider = new ethers.JsonRpcProvider(rpcUrl);
      
      // 获取网络信息
      const network = await provider.getNetwork();
      const chainId = Number(network.chainId);
      
      // 根据chainId获取主币符号
      const getSymbolByChainId = (chainId: number): string => {
        const knownChains: { [key: number]: string } = {
          1: 'ETH',      // Ethereum
          56: 'BNB',     // BSC
          137: 'MATIC',  // Polygon
          10: 'ETH',     // Optimism
          42161: 'ETH',  // Arbitrum
          8453: 'ETH',   // Base
          43114: 'AVAX', // Avalanche
          250: 'FTM',    // Fantom
          25: 'CRO',     // Cronos
          100: 'xDAI',   // Gnosis
          1285: 'MOVR',  // Moonriver
          1284: 'GLMR',  // Moonbeam
          66: 'OKT',     // OKC
          128: 'HT',     // HECO
          321: 'KCS',    // KCC
          1666600000: 'ONE', // Harmony
          2000: 'DOGE',  // Dogechain
          288: 'BOBA',   // Boba
          42220: 'CELO', // Celo
          1313161554: 'ETH', // Aurora
          592: 'ASTAR', // Astar
          336: 'SHM',   // Shiden
          1088: 'METIS', // Metis
          199: 'BTT',   // BitTorrent
          40: 'TLOS',   // Telos
          122: 'FUSE',  // Fuse
          50: 'XDC',    // XDC Network
          20: 'ELA',    // Elastos
          19: 'SGB',    // Songbird
          14: 'FLR',    // Flare
        };
        
        return knownChains[chainId] || 'ETH'; // 默认为ETH
      };

      const symbol = getSymbolByChainId(chainId);
      
      setChainInfo({
        chainId,
        symbol,
        isValid: true
      });

      message.success(`成功识别网络: Chain ID ${chainId}, 主币 ${symbol}`);
    } catch (error) {
      console.error('获取链信息失败:', error);
      setChainInfo({ chainId: null, symbol: '', isValid: false });
      message.error('无法连接到该RPC节点，请检查URL是否正确');
    } finally {
      setCustomNetworkForm(prev => ({ ...prev, isLoading: false }));
    }
  };

  // 添加自定义网络
  const handleAddCustomNetwork = () => {
    if (!customNetworkForm.name.trim()) {
      message.error('请输入网络名称');
      return;
    }
    
    if (!customNetworkForm.rpcUrl.trim()) {
      message.error('请输入RPC URL');
      return;
    }
    
    if (!chainInfo.isValid || chainInfo.chainId === null) {
      message.error('请先验证RPC URL以获取链信息');
      return;
    }

    // 检查是否已存在相同的网络
    if (allNetworks.some(network => 
      network.rpcUrl.toLowerCase() === customNetworkForm.rpcUrl.toLowerCase() ||
      (network.chainId === chainInfo.chainId && network.name === customNetworkForm.name)
    )) {
      message.warning('该网络已存在');
      return;
    }

    const newCustomNetwork: CustomNetwork = {
      id: `custom-${Date.now()}`,
      name: customNetworkForm.name,
      rpcUrl: customNetworkForm.rpcUrl,
      chainId: chainInfo.chainId,
      symbol: chainInfo.symbol,
      explorer: customNetworkForm.explorer || undefined,
      isCustom: true
    };

    const newCustomNetworks = [...customNetworks, newCustomNetwork];
    setCustomNetworks(newCustomNetworks);
    setAllNetworks([...NETWORK_CONFIGS, ...newCustomNetworks]);
    
    // 重置表单
    setCustomNetworkForm({ name: '', rpcUrl: '', explorer: '', isLoading: false });
    setChainInfo({ chainId: null, symbol: '', isValid: false });
    setIsCustomNetworkModalVisible(false);
    
    message.success(`成功添加自定义网络: ${newCustomNetwork.name}`);
  };

  // 删除自定义网络
  const handleDeleteCustomNetwork = (networkId: string) => {
    const filteredCustomNetworks = customNetworks.filter(network => network.id !== networkId);
    setCustomNetworks(filteredCustomNetworks);
    setAllNetworks([...NETWORK_CONFIGS, ...filteredCustomNetworks]);
    
    // 如果当前选中的网络被删除，切换到默认网络
    const deletedNetwork = customNetworks.find(network => network.id === networkId);
    if (deletedNetwork && config.rpcUrl === deletedNetwork.rpcUrl) {
      setConfig(prev => ({
        ...prev,
        rpcUrl: NETWORK_CONFIGS[0].rpcUrl,
        token: {
          address: 'native',
          symbol: NETWORK_CONFIGS[0].symbol,
          decimals: 18,
          name: 'Native Token'
        }
      }));
    }
    
    message.success('自定义网络已删除');
  };

  // 从localStorage加载自定义代币
  useEffect(() => {
    const savedTokens = localStorage.getItem('evmBalanceChecker_customTokens');
    if (savedTokens) {
      try {
        setCustomTokens(JSON.parse(savedTokens));
      } catch (error) {
        console.error('加载自定义代币失败:', error);
      }
    }
  }, []);

  // 保存自定义代币到localStorage
  useEffect(() => {
    localStorage.setItem('evmBalanceChecker_customTokens', JSON.stringify(customTokens));
  }, [customTokens]);

  // 从localStorage加载自定义网络
  useEffect(() => {
    const savedNetworks = localStorage.getItem('evmBalanceChecker_customNetworks');
    if (savedNetworks) {
      try {
        const parsedNetworks = JSON.parse(savedNetworks) as CustomNetwork[];
        setCustomNetworks(parsedNetworks);
        setAllNetworks([...NETWORK_CONFIGS, ...parsedNetworks]);
      } catch (error) {
        console.error('加载自定义网络失败:', error);
      }
    }
  }, []);

  // 保存自定义网络到localStorage
  useEffect(() => {
    localStorage.setItem('evmBalanceChecker_customNetworks', JSON.stringify(customNetworks));
  }, [customNetworks]);

  // 监听网络变化，更新原生代币和浏览器
  useEffect(() => {
    const network = allNetworks.find(n => n.rpcUrl === config.rpcUrl);
    if (network) {
      setCurrentExplorer(network.explorer || '');
      if (config.token.address === 'native') {
        setConfig(prev => ({
          ...prev,
          token: { ...prev.token, symbol: network.symbol }
        }));
      }
    }
  }, [config.rpcUrl, allNetworks]);

  // 导入钱包地址
  const handleImportWallets = () => {
    if (!addressesInput.trim()) {
      message.error('请输入钱包地址');
      return;
    }

    const addresses = addressesInput
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);

    const validAddresses: string[] = [];
    const invalidAddresses: string[] = [];

    addresses.forEach((address, index) => {
      if (isValidAddress(address)) {
        validAddresses.push(address);
      } else {
        invalidAddresses.push(`第${index + 1}行`);
      }
    });

    if (invalidAddresses.length > 0) {
      message.error(`发现${invalidAddresses.length}个无效地址: ${invalidAddresses.join(', ')}`);
      return;
    }

    if (validAddresses.length === 0) {
      message.error('没有有效的地址');
      return;
    }

    // 去重处理
    const existingAddresses = new Set(wallets.map(w => w.address.toLowerCase()));
    const newAddresses = validAddresses.filter(addr => 
      !existingAddresses.has(addr.toLowerCase())
    );

    if (newAddresses.length === 0) {
      message.warning('所有地址都已存在');
      return;
    }

    const newWallets: WalletEntry[] = newAddresses.map((address, index) => ({
      id: Date.now() + index,
      address,
      balance: '0',
      status: 'idle',
      isSelected: false
    }));

    setWallets(prev => [...prev, ...newWallets]);
    setIsImportModalVisible(false);
    setAddressesInput('');
    message.success(`成功导入${newWallets.length}个地址`);
  };

  // 添加自定义代币
  const handleAddToken = async () => {
    if (!tokenAddressInput.trim() || !isValidAddress(tokenAddressInput)) {
      message.error('请输入有效的代币合约地址');
      return;
    }

    try {
      const provider = new ethers.JsonRpcProvider(config.rpcUrl);
      const tokenContract = new ethers.Contract(tokenAddressInput, ERC20_ABI, provider);

      const [name, symbol, decimals] = await Promise.all([
        tokenContract.name(),
        tokenContract.symbol(),
        tokenContract.decimals()
      ]);

      const newToken: TokenInfo = {
        address: tokenAddressInput,
        name,
        symbol,
        decimals: Number(decimals)
      };

      // 检查是否已存在
      if (customTokens.some(token => token.address.toLowerCase() === tokenAddressInput.toLowerCase())) {
        message.warning('该代币已存在');
        return;
      }

      setCustomTokens(prev => [...prev, newToken]);
      setIsTokenModalVisible(false);
      setTokenAddressInput('');
      message.success(`成功添加代币: ${symbol} (${name})`);
    } catch (error) {
      console.error('添加代币失败:', error);
      message.error('添加代币失败，请检查合约地址和网络连接');
    }
  };

  // 筛选功能
  const handleFilter = (type: 'zero' | 'positive' | 'invert' | 'value') => {
    const updatedWallets = wallets.map(wallet => {
      const balance = parseFloat(wallet.balance || '0');
      let shouldSelect = false;

      switch (type) {
        case 'zero':
          shouldSelect = balance === 0;
          break;
        case 'positive':
          shouldSelect = balance > 0;
          break;
        case 'invert':
          shouldSelect = !wallet.isSelected;
          break;
        case 'value':
          const filterNum = parseFloat(filterValue || '0');
          shouldSelect = balance >= filterNum;
          break;
      }

      return { ...wallet, isSelected: shouldSelect };
    });

    setWallets(updatedWallets);
    const selectedCount = updatedWallets.filter(w => w.isSelected).length;
    message.success(`筛选完成，已选中${selectedCount}个地址`);
  };

  // 删除选中的地址
  const handleDeleteSelected = () => {
    const selectedCount = wallets.filter(w => w.isSelected).length;
    if (selectedCount === 0) {
      message.warning('没有选中的地址');
      return;
    }

    setWallets(prev => prev.filter(wallet => !wallet.isSelected));
    message.success(`已删除${selectedCount}个地址`);
  };

  // 导出选中的地址
  const handleExportSelected = () => {
    const selectedWallets = wallets.filter(w => w.isSelected);
    if (selectedWallets.length === 0) {
      message.warning('没有选中的地址');
      return;
    }

    const csvContent = [
      'Address,Balance,Status',
      ...selectedWallets.map(wallet =>
        `${wallet.address},${wallet.balance},${wallet.status}`
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const fileName = `EVM余额查询结果_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`;
    saveAs(blob, fileName);

    message.success('导出成功');
  };

  // 批处理工具函数
  const processInBatches = async <T,>(
    items: T[],
    batchSize: number,
    processFn: (item: T) => Promise<void>
  ): Promise<void> => {
    for (let i = 0; i < items.length; i += batchSize) {
      const batch = items.slice(i, i + batchSize);
      await Promise.all(batch.map(item => processFn(item)));
    }
  };

  // 单个钱包余额查询函数
  const fetchBalanceForWallet = async (wallet: WalletEntry): Promise<void> => {
    try {
      // 更新状态为加载中
      setWallets(prev => prev.map(w =>
        w.id === wallet.id ? { ...w, status: 'loading' } : w
      ));

      const provider = new ethers.JsonRpcProvider(config.rpcUrl);
      let balance: bigint;
      let formattedBalance: string;

      if (config.token.address === 'native') {
        // 查询原生代币余额
        balance = await provider.getBalance(wallet.address);
        formattedBalance = ethers.formatEther(balance);
      } else {
        // 查询ERC20代币余额
        const tokenContract = new ethers.Contract(config.token.address, ERC20_ABI, provider);
        balance = await tokenContract.balanceOf(wallet.address);
        formattedBalance = ethers.formatUnits(balance, config.token.decimals);
      }

      // 更新钱包余额和状态
      setWallets(prev => prev.map(w =>
        w.id === wallet.id ? {
          ...w,
          balance: parseFloat(formattedBalance).toFixed(6),
          status: 'success'
        } : w
      ));

      // 累加到总余额
      setTotalBalance(prev => {
        const prevBalance = ethers.parseUnits(prev || '0', config.token.decimals);
        const newTotal = prevBalance + balance;
        return ethers.formatUnits(newTotal, config.token.decimals);
      });

    } catch (error) {
      console.error(`查询余额失败 ${wallet.address}:`, error);

      // 更新状态为错误
      setWallets(prev => prev.map(w =>
        w.id === wallet.id ? { ...w, status: 'error', balance: '0' } : w
      ));
    } finally {
      // 更新进度
      setQueryProgress(prev => ({ ...prev, checked: prev.checked + 1 }));
    }
  };

  // 核心查询余额功能
  const handleQueryBalances = async () => {
    if (wallets.length === 0) {
      message.warning('请先导入钱包地址');
      return;
    }

    // 初始化
    setIsQuerying(true);
    setQueryProgress({ checked: 0, total: wallets.length });
    setTotalBalance('0');

    // 重置所有钱包状态
    setWallets(prev => prev.map(wallet => ({
      ...wallet,
      status: 'idle',
      balance: '0'
    })));

    try {
      message.info('开始批量查询余额任务...');

      // 执行批量查询
      await processInBatches(wallets, config.batchSize, fetchBalanceForWallet);

      message.success('所有余额查询任务已完成');
    } catch (error) {
      console.error('批量查询失败:', error);
      message.error('批量查询失败');
    } finally {
      setIsQuerying(false);
    }
  };

  return (
    <div className="container-responsive">
      {/* 标题 */}
      <h1 style={{ 
        textAlign: 'center', 
        fontSize: 'var(--font-xl)',
        fontWeight: 'bold',
        marginBottom: 'var(--spacing-2xl)',
        color: '#333'
      }}>
        EVM批量余额查询工具
      </h1>

      {/* 顶部操作按钮 */}
      <Card className="spacing-responsive">
        <Space wrap>
          <Button
            icon={<WalletOutlined />}
            onClick={() => setIsImportModalVisible(true)}
            style={buttonStyle}
          >
            导入钱包
          </Button>
          <Button
            icon={<FilterOutlined />}
            onClick={() => handleFilter('zero')}
            style={buttonStyle}
          >
            余额为0
          </Button>
          <Button
            icon={<FilterOutlined />}
            onClick={() => handleFilter('positive')}
            style={buttonStyle}
          >
            余额大于0
          </Button>
          <Button
            onClick={() => handleFilter('invert')}
            style={buttonStyle}
          >
            反选
          </Button>
          <Input
            placeholder="余额 >="
            value={filterValue}
            onChange={(e) => setFilterValue(e.target.value)}
            style={{ ...componentStyle, width: '120px' }}
            suffix={
              <Button
                type="text"
                size="small"
                onClick={() => handleFilter('value')}
              >
                筛选
              </Button>
            }
          />
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={handleDeleteSelected}
            style={buttonStyle}
          >
            删除选中
          </Button>
          <Button
            icon={<ExportOutlined />}
            onClick={handleExportSelected}
            style={buttonStyle}
          >
            导出选中
          </Button>
        </Space>
      </Card>

      {/* 钱包信息表格 */}
      {wallets.length > 0 && (
        <Card className="spacing-responsive">
          <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '500' }}>
              钱包列表 ({wallets.length}个)
            </h3>
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => {
                setWallets([]);
                setQueryProgress({ checked: 0, total: 0 });
                setTotalBalance('0');
              }}
              style={buttonStyle}
            >
              清空全部
            </Button>
          </div>

          <Table
            columns={[
              {
                title: '序号',
                dataIndex: 'index',
                width: 60,
                render: (_, __, index) => index + 1,
              },
              {
                title: '钱包地址',
                dataIndex: 'address',
                width: 300,
                render: (text: string) => (
                  <Space>
                    <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                      {text.slice(0, 6)}...{text.slice(-4)}
                    </span>
                    <Tooltip title="复制地址">
                      <Button
                        type="text"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => copyToClipboard(text, '地址')}
                      />
                    </Tooltip>
                    <Tooltip title="在区块链浏览器中查看">
                      <Button
                        type="text"
                        size="small"
                        icon={<LinkOutlined />}
                        onClick={() => window.open(`${currentExplorer}${text}`, '_blank')}
                      />
                    </Tooltip>
                  </Space>
                ),
              },
              {
                title: `${config.token.symbol}余额`,
                dataIndex: 'balance',
                width: 150,
                render: (text: string, record: WalletEntry) => (
                  <Space>
                    <span>{text}</span>
                    {record.status === 'loading' && <Tag color="processing">查询中</Tag>}
                    {record.status === 'error' && <Tag color="error">失败</Tag>}
                    {record.status === 'success' && <Tag color="success">成功</Tag>}
                  </Space>
                ),
              },
              {
                title: '操作',
                width: 80,
                render: (_, record: WalletEntry) => (
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => {
                      setWallets(prev => prev.filter(wallet => wallet.id !== record.id));
                    }}
                  />
                ),
              },
            ]}
            dataSource={wallets}
            rowKey="id"
            rowSelection={{
              selectedRowKeys: wallets.filter(w => w.isSelected).map(w => w.id),
              onChange: (selectedRowKeys) => {
                setWallets(prev => prev.map(wallet => ({
                  ...wallet,
                  isSelected: selectedRowKeys.includes(wallet.id)
                })));
              },
              onSelectAll: (selected, selectedRows, changeRows) => {
                // 全选/取消全选所有钱包（不仅仅是当前页）
                setWallets(prev => prev.map(wallet => ({
                  ...wallet,
                  isSelected: selected
                })));
              },
              onSelect: (record, selected, selectedRows, nativeEvent) => {
                // 单选处理
                setWallets(prev => prev.map(wallet =>
                  wallet.id === record.id ? { ...wallet, isSelected: selected } : wallet
                ));
              },
            }}
            pagination={false}
            scroll={{ x: 800, y: 400 }}
            size="small"
          />

          {/* 表格底部汇总 */}
          <div style={{
            marginTop: '16px',
            padding: '12px',
            background: '#f5f5f5',
            borderRadius: '6px',
            fontSize: '14px'
          }}>
            <Row gutter={16}>
              <Col span={8}>
                <strong>总选中: {wallets.filter(w => w.isSelected).length} 个</strong>
              </Col>
              <Col span={8}>
                <strong>查询进度: {queryProgress.checked} / {queryProgress.total}</strong>
              </Col>
              <Col span={8}>
                <strong>总余额: {totalBalance} {config.token.symbol}</strong>
              </Col>
            </Row>
          </div>
        </Card>
      )}

      {/* 底部配置与操作区域 */}
      <Card className="spacing-responsive">
        <h3 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: '500' }}>
          查询配置
        </h3>

        {/* 选择链 & 节点 */}
        <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
          <Col xs={24} sm={12}>
            <div>
              <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                选择网络
              </h4>
              <Select
                value={config.rpcUrl}
                onChange={(value) => {
                  setConfig(prev => ({ ...prev, rpcUrl: value }));
                }}
                style={{ ...componentStyle, width: '100%' }}
                placeholder="选择网络"
                dropdownRender={(menu) => (
                  <>
                    {menu}
                    <Divider style={{ margin: '8px 0' }} />
                    <Space style={{ padding: '0 8px 4px' }}>
                      <Button
                        type="text"
                        icon={<PlusOutlined />}
                        onClick={() => setIsCustomNetworkModalVisible(true)}
                        style={{ fontSize: '12px' }}
                      >
                        添加自定义网络
                      </Button>
                    </Space>
                  </>
                )}
              >
                <Select.OptGroup label="预设网络">
                  {NETWORK_CONFIGS.map(network => (
                    <Select.Option key={network.id} value={network.rpcUrl}>
                      {network.name}
                    </Select.Option>
                  ))}
                </Select.OptGroup>
                {customNetworks.length > 0 && (
                  <Select.OptGroup label="自定义网络">
                    {customNetworks.map(network => (
                      <Select.Option key={network.id} value={network.rpcUrl}>
                        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                          <span>{network.name} (Chain ID: {network.chainId})</span>
                          <Button
                            type="text"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteCustomNetwork(network.id);
                            }}
                            style={{ fontSize: '12px', padding: '0 4px' }}
                          />
                        </Space>
                      </Select.Option>
                    ))}
                  </Select.OptGroup>
                )}
              </Select>
            </div>
          </Col>

          <Col xs={24} sm={12}>
            <div>
              <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                自定义RPC节点
              </h4>
              <Input
                value={config.rpcUrl}
                onChange={(e) => setConfig(prev => ({ ...prev, rpcUrl: e.target.value }))}
                style={componentStyle}
                placeholder="输入自定义RPC URL"
              />
            </div>
          </Col>
        </Row>

        {/* 查询模式 */}
        <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
          <Col xs={24} sm={12}>
            <div>
              <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                查询模式
              </h4>
              <Radio.Group
                value={config.queryMode}
                onChange={(e) => {
                  const mode = e.target.value;
                  setConfig(prev => ({
                    ...prev,
                    queryMode: mode,
                    batchSize: mode === 'compatible' ? 1 : 10
                  }));
                }}
                style={{ width: '100%' }}
              >
                <Space direction="vertical">
                  <Radio value="compatible">兼容模式 (逐个查询，稳定性高)</Radio>
                  <Radio value="speed">极速模式 (并发查询，速度快)</Radio>
                </Space>
              </Radio.Group>
            </div>
          </Col>

          <Col xs={24} sm={12}>
            <div>
              <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                并发数量 {config.queryMode === 'compatible' && <Tag color="orange">兼容模式固定为1</Tag>}
              </h4>
              <InputNumber
                value={config.batchSize}
                onChange={(value) => setConfig(prev => ({ ...prev, batchSize: value || 1 }))}
                min={1}
                max={50}
                style={{ ...componentStyle, width: '100%' }}
                disabled={config.queryMode === 'compatible'}
                placeholder="并发查询数量"
              />
            </div>
          </Col>
        </Row>

        {/* 选择代币 */}
        <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
          <Col xs={24} sm={16}>
            <div>
              <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                选择代币
              </h4>
              <Select
                value={config.token.address}
                onChange={(value) => {
                  if (value === 'native') {
                    const network = NETWORK_CONFIGS.find(n => n.rpcUrl === config.rpcUrl);
                    setConfig(prev => ({
                      ...prev,
                      token: {
                        address: 'native',
                        symbol: network?.symbol || 'ETH',
                        decimals: 18,
                        name: 'Native Token'
                      }
                    }));
                  } else {
                    const token = customTokens.find(t => t.address === value);
                    if (token) {
                      setConfig(prev => ({ ...prev, token }));
                    }
                  }
                }}
                style={{ ...componentStyle, width: '100%' }}
                placeholder="选择代币"
              >
                <Select.Option value="native">
                  {config.token.address === 'native' ? config.token.symbol : 'ETH'} (原生代币)
                </Select.Option>
                {customTokens.map(token => (
                  <Select.Option key={token.address} value={token.address}>
                    {token.symbol} - {token.name}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </Col>

          <Col xs={24} sm={8}>
            <div style={{ paddingTop: '24px' }}>
              <Button
                onClick={() => setIsTokenModalVisible(true)}
                style={buttonStyle}
                icon={<PlusOutlined />}
              >
                添加代币
              </Button>
            </div>
          </Col>
        </Row>

        {/* 其他异构链导航 */}
        <Divider>其他链余额查询</Divider>
        <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
          <Col span={24}>
            <Space size="large" style={{ width: '100%', justifyContent: 'center' }}>
              <Tooltip title="Solana余额查询">
                <Button
                  style={buttonStyle}
                  onClick={() => window.open('/query/batch-balance-sol', '_self')}
                >
                  <img src="/icons/solana.svg" alt="Solana" style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                  Solana
                </Button>
              </Tooltip>
              <Tooltip title="Sui余额查询">
                <Button
                  style={buttonStyle}
                  onClick={() => window.open('/query/batch-balance-sui', '_self')}
                >
                  <img src="/icons/sui.svg" alt="Sui" style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                  Sui
                </Button>
              </Tooltip>
              <Tooltip title="TON余额查询">
                <Button
                  style={buttonStyle}
                  onClick={() => window.open('/query/batch-balance-ton', '_self')}
                >
                  <img src="/icons/ton.svg" alt="TON" style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                  TON
                </Button>
              </Tooltip>
              <Tooltip title="Bitcoin余额查询">
                <Button
                  style={buttonStyle}
                  onClick={() => window.open('/query/batch-balance-btc', '_self')}
                >
                  <img src="/icons/bitcoin.svg" alt="Bitcoin" style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                  Bitcoin
                </Button>
              </Tooltip>
            </Space>
          </Col>
        </Row>

        {/* 底部汇总与操作按钮 */}
        <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
          <Col xs={24} sm={16}>
            <div style={{
              padding: '12px',
              background: '#f0f9ff',
              borderRadius: '6px',
              border: '1px solid #bae6fd'
            }}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div style={{ fontSize: '14px', fontWeight: '500' }}>
                  📊 查询统计
                </div>
                <div style={{ fontSize: '13px', color: '#666' }}>
                  总地址: {wallets.length} 个 |
                  已查询: {queryProgress.checked} 个 |
                  剩余: {queryProgress.total - queryProgress.checked} 个
                </div>
                <div style={{ fontSize: '13px', color: '#666' }}>
                  累计余额: <strong style={{ color: '#1890ff' }}>{totalBalance} {config.token.symbol}</strong>
                </div>
                {isQuerying && (
                  <Progress
                    percent={queryProgress.total > 0 ? Math.round((queryProgress.checked / queryProgress.total) * 100) : 0}
                    size="small"
                    status="active"
                  />
                )}
              </Space>
            </div>
          </Col>

          <Col xs={24} sm={8}>
            <div style={{ textAlign: 'center', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Button
                type="primary"
                size="large"
                icon={<SearchOutlined />}
                onClick={handleQueryBalances}
                disabled={wallets.length === 0 || isQuerying}
                loading={isQuerying}
                style={{
                  ...primaryButtonStyle,
                  minWidth: '160px',
                  height: '44px',
                  fontSize: '16px'
                }}
              >
                {isQuerying ? '查询中...' : '查询余额'}
              </Button>
            </div>
          </Col>
        </Row>
      </Card>

      {/* 导入钱包地址模态框 */}
      <Modal
        title="导入钱包地址"
        open={isImportModalVisible}
        onOk={handleImportWallets}
        onCancel={() => {
          setIsImportModalVisible(false);
          setAddressesInput('');
        }}
        width={600}
        okText="确认导入"
        cancelText="取消"
      >
        <Alert
          message="批量导入钱包地址"
          description="请粘贴钱包地址，每行一个。系统会自动验证地址格式并去重。"
          type="info"
          showIcon
          style={{ marginBottom: '16px' }}
        />
        <Form layout="vertical">
          <Form.Item
            label="钱包地址列表 (每行一个地址)"
            required
          >
            <Input.TextArea
              value={addressesInput}
              onChange={(e) => setAddressesInput(e.target.value)}
              placeholder="请粘贴钱包地址，每行一个&#10;例如：&#10;0x1234567890abcdef...&#10;0xabcdef1234567890..."
              rows={10}
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 添加代币模态框 */}
      <Modal
        title="添加自定义代币"
        open={isTokenModalVisible}
        onOk={handleAddToken}
        onCancel={() => {
          setIsTokenModalVisible(false);
          setTokenAddressInput('');
        }}
        width={500}
        okText="添加代币"
        cancelText="取消"
      >
        <Form layout="vertical">
          <Form.Item
            label="代币合约地址"
            required
          >
            <Input
              value={tokenAddressInput}
              onChange={(e) => setTokenAddressInput(e.target.value)}
              placeholder="输入ERC20代币合约地址"
              style={componentStyle}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 添加自定义网络模态框 */}
      <Modal
        title="添加自定义网络"
        open={isCustomNetworkModalVisible}
        onOk={handleAddCustomNetwork}
        onCancel={() => {
          setIsCustomNetworkModalVisible(false);
          setCustomNetworkForm({ name: '', rpcUrl: '', explorer: '', isLoading: false });
          setChainInfo({ chainId: null, symbol: '', isValid: false });
        }}
        width={600}
        okText="添加网络"
        cancelText="取消"
        confirmLoading={customNetworkForm.isLoading}
      >
        <Alert
          message="添加自定义EVM网络"
          description="输入RPC URL后系统会自动识别链ID和主币信息。请确保RPC节点稳定可用。"
          type="info"
          showIcon
          style={{ marginBottom: '16px' }}
        />
        
        <Form layout="vertical">
          <Form.Item
            label="网络名称"
            required
          >
            <Input
              value={customNetworkForm.name}
              onChange={(e) => setCustomNetworkForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="例如: My Custom Network"
              style={componentStyle}
            />
          </Form.Item>

          <Form.Item
            label="RPC URL"
            required
          >
            <Space.Compact style={{ width: '100%' }}>
              <Input
                value={customNetworkForm.rpcUrl}
                onChange={(e) => {
                  const value = e.target.value;
                  setCustomNetworkForm(prev => ({ ...prev, rpcUrl: value }));
                  setChainInfo({ chainId: null, symbol: '', isValid: false });
                }}
                placeholder="https://..."
                style={componentStyle}
              />
              <Button
                type="primary"
                loading={customNetworkForm.isLoading}
                onClick={() => fetchChainInfo(customNetworkForm.rpcUrl)}
                style={buttonStyle}
              >
                检测
              </Button>
            </Space.Compact>
          </Form.Item>

          {chainInfo.isValid && (
            <Alert
              message="网络信息检测成功"
              description={
                <div>
                  <p><strong>链ID:</strong> {chainInfo.chainId}</p>
                  <p><strong>主币符号:</strong> {chainInfo.symbol}</p>
                </div>
              }
              type="success"
              showIcon
              style={{ marginBottom: '16px' }}
            />
          )}

          <Form.Item
            label="区块链浏览器 (可选)"
          >
            <Input
              value={customNetworkForm.explorer}
              onChange={(e) => setCustomNetworkForm(prev => ({ ...prev, explorer: e.target.value }))}
              placeholder="https://explorer.example.com/address/"
              style={componentStyle}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 使用提示 */}
      <Card size="small" style={{
        background: '#f6ffed',
        border: '1px solid #b7eb8f',
        marginTop: '20px'
      }}>
        <div style={{ color: '#52c41a' }}>
          <strong>💡 使用提示：</strong>
          <ul style={{ margin: '8px 0 0 20px', paddingLeft: 0 }}>
            <li>兼容模式：逐个查询，稳定性高，适合大量地址或网络不稳定时使用</li>
            <li>极速模式：并发查询，速度快，适合少量地址或网络稳定时使用</li>
            <li>支持查询原生代币（ETH/BNB等）和自定义ERC20代币余额</li>
            <li>支持添加自定义EVM网络，系统会自动识别链ID和主币信息</li>
            <li>可以按余额条件筛选地址，支持导出查询结果</li>
            <li>自定义代币和网络会自动保存到本地，下次使用时无需重新添加</li>
          </ul>
        </div>
      </Card>
    </div>
  );
};

export default EvmBalanceChecker;
